/// <amd-module name="Wit/Search/InstantSearchProviders/RecentBoardsProvider" />
import * as React from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Services, IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { ResultRow } from "InstantSearch/Shared/ResultRow";
import { IMenuServiceContext, IInstantSearchMenuService, IRecentBoardsMenuProps } from "InstantSearch/Shared/Types";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { DayZeroMessageProvider } from "Wit/Search/InstantSearchProviders/DayZeroMessageProvider";
import { RecentActivitySecuredData } from "Wit/Search/Generated/Contracts";
import * as Resources from "Wit/Search/Resources";

const maxResultRows = 5;

/**
 * Service for creating menu items for recent board items.
 */
export class RecentBoardsProvider extends BaseInstantSearchProvider<RecentActivitySecuredData[] | undefined> implements IInstantSearchMenuService<IMenuServiceContext> {
    private _contributionId = "ms.vss-work-web.recent-boards-menu-provider";
    private _recentBoardsProps: { [id: string]: IRecentBoardsMenuProps };
    private _dayZeroMessageService: DayZeroMessageProvider;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initContribution(this._contributionId);
        if (this._contribution) {
            this._recentBoardsProps = this._contribution.recentBoardsMenuProps || {};
        }
        this._dayZeroMessageService = this.pageContext.getService<DayZeroMessageProvider>("dayzero-message-provider-service");
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && !searchText) {
            this.getSuggestions("", context, "RecentBoardsFetched");
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<RecentActivitySecuredData[] | undefined> {
        const dataProviderId = "ms.vss-work-web.boards-recent-activity-data-provider";
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const { invokedProvidersProps } = context;
        const key = invokedProvidersProps && invokedProvidersProps.props ? invokedProvidersProps.props.key : undefined;
        const boardType = key ? key.substr(1) : "";
        const parameters = boardType ? { boardType: boardType } : undefined;
        return contributionService.getDataAsync<RecentActivitySecuredData[]>(dataProviderId, parameters, true);
    }

    protected _onFetchSuccess(searchText: string, context: IMenuServiceContext, data: RecentActivitySecuredData[] | undefined): void {
        const { telemetryWriter, onFocus, searchInput, invokedProvidersProps } = context;
        this._menuItems.splice(0, this._menuItems.length);

        if (this.shouldInvokeProvider(context) && searchInput.queryText().trim()) {
            // Search text is non-empty. It got changed before fetching results. So, not updating menuItems.
            return;
        }

        if (data && data.length > 0) {
            const header = invokedProvidersProps && invokedProvidersProps.props
                ? invokedProvidersProps.props.emptyTextHeader
                : Resources.RecentBoardsHeader;
            this._dayZeroMessageService.updateBoardState(true);
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            });

            const limitOfWorkItems = Math.min(maxResultRows, data.length);
            for (let index = 0; index < limitOfWorkItems; index++) {
                const recentBoard: RecentActivitySecuredData = data[index];
                this._menuItems.push({
                    id: "",
                    groupKey: this._groupKey,
                    rank: this._rank,
                    renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined => {
                        details.onFocusItem = onFocus;
                        switch (menuCell) {
                            case MenuCell.PrimaryText:
                                var recentBoardsProperties = this._recentBoardsProps[recentBoard.artifactKind.toUpperCase()];
                                const boardResultString = format("{0} {1}", recentBoard.name, recentBoardsProperties.boardType);
                                return <ResultRow iconProps={{ iconName: recentBoardsProperties.iconName }} title={boardResultString}></ResultRow>;
                            default:
                                return undefined;
                        }
                    },
                    onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) => {
                        if (event.ctrlKey || event.metaKey) {
                            const newWindow = window.open(recentBoard.artifactUri, "_blank");
                            if (newWindow && newWindow.opener) {
                                newWindow.opener = null;
                            }
                        } else {
                            onClickFPS(this.pageContext, recentBoard.artifactUri, true, event);
                        }
                        this._recordWorkItemClickTelemetry(index, telemetryWriter);
                    }
                });
            }
        } else if ((data && data.length === 0) || !data) {
            this._dayZeroMessageService.updateBoardState(false);
            telemetryWriter.publish("NoSuggestionsFound", { "type": "RecentBoards" });
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: Error): void {
        const { invokedProvidersProps } = context;
        const header = invokedProvidersProps && invokedProvidersProps.props
            ? invokedProvidersProps.props.emptyTextHeader
            : Resources.RecentBoardsHeader;
        this._menuItems.splice(0, this._menuItems.length);
        this._menuItems.push(
            {
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: header,
                className: "search-suggestion-header"
            },
            {
                id: "",
                itemType: MenuItemType.Header,
                className: "suggestion-message",
                groupKey: this._groupKey,
                rank: this._rank,
                text: Resources.GenericErrorMessage
            });
        announce(Resources.GenericErrorMessage);
    }

    private _recordWorkItemClickTelemetry = (index: number, telemetryWriter: TelemetryWriter): void => {
        telemetryWriter.publish("ResultClicked", {
            "index": index,
            "resultType": "RecentBoards"
        });
    }
}

Services.add("recent-boards-provider-service", { serviceFactory: RecentBoardsProvider });